import { GTM_LANGUAGE_EVENTS } from '@aca-new/app/shared/constants/gtm-analytics.constants';
import { AppcuesService } from '@aca-new/app/shared/services/third-party-services/appcues/appcues.service';
import { GoogleAnalyticsService } from '@aca-new/app/shared/services/third-party-services/google-analytics/google-analytics.service';
import { LogrocketInitService } from '@aca-new/app/shared/services/third-party-services/logrocket-init/logrocket-init.service';
import { inject, Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class UserLanguageTrackingService {
  private readonly _translateService: TranslateService = inject(TranslateService);
  private readonly _googleAnalyticsService: GoogleAnalyticsService = inject(GoogleAnalyticsService);
  private readonly _logrocketInitService: LogrocketInitService = inject(LogrocketInitService);
  private readonly _appcuesService: AppcuesService = inject(AppcuesService);

  /**
   * @description
   * Subscribes to language changes and syncs the current language to analytics vendors.
   * @returns {void} Nothing.
   */
  public init(): void {
    this._translateService.onLangChange
      .pipe(
        startWith({ lang: this._translateService.currentLang }),
        map((event): string => event.lang),
        filter((language): boolean => !!language),
        distinctUntilChanged(),
        untilDestroyed(this)
      )
      .subscribe((language: string): void => {
        this.syncSelectedLanguage(language);
      });
  }

  /**
   * @description
   * Pushes the selected UI language to Google Analytics, LogRocket, and AppCues.
   * @param {string} language - Active translate locale (same codes as the language selector).
   * @returns {void} Nothing.
   */
  public syncSelectedLanguage(language: string): void {
    /* eslint-disable @typescript-eslint/naming-convention -- GTM dataLayer keys */
    this._googleAnalyticsService.sendEvent({
      event: GTM_LANGUAGE_EVENTS.contextUpdate,
      selected_language: language,
    });
    /* eslint-enable @typescript-eslint/naming-convention */

    this._logrocketInitService.syncSelectedLanguage(language);
    this._appcuesService.syncSelectedLanguage(language);
  }
}
